import { useCallback, useEffect } from 'react';
import { WORK_STATE_ORIGIN } from './useWorkManagement';
import { useSetAtom, useAtomValue } from 'jotai';
import type { NotificationEventFrame, ErrorEventFrame } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { addErrorEntryAction } from '../stores/errors/atoms';
import {
  addNotificationEntryAction,
  notificationMutePrefsAtom,
  type NotificationKind,
} from '../stores/notifications/atoms';
import { resolveErrorMessage } from '../components/errors/error-messages';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { usePushFrameSubscription } from './usePushFrameSubscription';
import type { ErrorSeverity } from '../types/error-events-types';

let entryCounter = 0;

/**
 * The ONE global bridge-demux subscriber — replaces the deleted
 * `useErrorEvents` (which polled `/v1/errors/list` every 5s via `ErrorWSManager`,
 * both now deleted). Mounted app-wide at `MainLayout` so BOTH errors and
 * notifications stream on ANY view: the nav badge + toasts update inside a Chat
 * session, not only inside Settings.
 *
 * Feeds BOTH stores from the shared bridge push frames (live + replay, dedup by
 * event_id in the SDK demux). It does NOT raise toasts — that is the
 * `NotificationToastDispatcher`'s job (also mounted globally).
 */
export function useNotificationStream(): void {
  const addError = useSetAtom(addErrorEntryAction);
  const addNotification = useSetAtom(addNotificationEntryAction);
  const setMutePrefs = useSetAtom(notificationMutePrefsAtom);
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  // (events ADR-006)
  useEffect(() => {
    // Gated like every other authenticated read (see useBilling): the route is
    // credentialed, so firing it before the AuthProvider holds a credential
    // cannot succeed — it only raises MissingCredentialError on every cold
    // renderer boot. This defers the read; it never substitutes a value for it.
    if (!isAuthenticated || !sdkConfigured) return;
    let cancelled = false;
    void (async () => {
      try {
        const prefs = await getSDK().notifications.getPrefs();
        if (cancelled) return;
        setMutePrefs({
          mutedKinds: prefs.muted_kinds.filter(
            (k): k is NotificationKind => k === 'info' || k === 'success',
          ),
          mutedOrigins: prefs.muted_origins,
        });
      } catch (error) {
        // Surfaced, not swallowed (.claude/rules/no-fallbacks.md): unmuted is
        // the loud outcome — rows the user muted reappear — so the failure is
        // visible in the product as well as here, and no state is faked.
        console.error('Failed to load notification mute preferences:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setMutePrefs, isAuthenticated, sdkConfigured]);

  const onError = useCallback(
    (frame: ErrorEventFrame) => {
      entryCounter++;
      addError({
        id: `error-${Date.now()}-${entryCounter}`,
        eventIds: [frame.event_id],
        error_code: frame.error_code,
        message: resolveErrorMessage(frame.error_code, frame.message),
        service_origin: frame.service_origin,
        severity: frame.severity as ErrorSeverity,
        workspace_id: frame.workspace_id,
        // Threaded end-to-end: the session-link + details
        // expander in Settings read these off the aggregated entry.
        session_id: frame.session_id,
        details: frame.details,
        timestamp: frame.timestamp,
        occurrenceCount: 1,
        read: frame.read === true,
      });
    },
    [addError],
  );

  const onNotification = useCallback(
    (frame: NotificationEventFrame) => {
      // Board-refresh signal, not a user-facing notice (app-shell ADR-012).
      if (frame.service_origin === WORK_STATE_ORIGIN) return;
      addNotification({
        id: `notif-${frame.event_id}`,
        eventId: frame.event_id,
        kind: frame.kind,
        title: frame.title,
        body: frame.body,
        serviceOrigin: frame.service_origin,
        sessionId: frame.session_id,
        workspaceId: frame.workspace_id,
        details: frame.details,
        timestamp: frame.created_at,
        read: false,
      });
    },
    [addNotification],
  );

  usePushFrameSubscription({ onNotification, onError });
}
